import React, { useState, useEffect } from 'react';
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface AssignmentComponentProps {
  assignment: any;
  courseId: string;
  moduleId: string;
  onComplete?: () => void;
}

interface AssignmentSubmission {
  id: string;
  submission: string;
  submissionType: string;
  fileName?: string;
  submittedAt: string;
  status: 'submitted' | 'graded' | 'pending';
  grade?: number;
  feedback?: string;
  gradedAt?: string;
  gradedBy?: string;
}

const AssignmentComponent: React.FC<AssignmentComponentProps> = ({
  assignment,
  courseId,
  moduleId,
  onComplete
}) => {
  const [submission, setSubmission] = useState('');
  const [submissionType, setSubmissionType] = useState<'text' | 'file' | 'url'>('text');
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState<AssignmentSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    fetchExistingSubmission();
  }, [assignment.id, courseId]);

  const fetchExistingSubmission = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/assignment/${courseId}/${assignment.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();
      if (result.success && result.data) {
        setExistingSubmission(result.data);
        setSubmission(result.data.submission);
        setSubmissionType(result.data.submissionType);
        setFileName(result.data.fileName || '');
        if (result.data.status === 'graded') {
          setShowFeedback(true);
        }
      }
    } catch (error) {
      console.error('Error fetching assignment submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!submission.trim()) {
      toast.error('Please provide your submission');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to submit assignment');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/assignment/${courseId}/${assignment.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            submission: submission.trim(),
            submissionType,
            fileName: fileName || null
          })
        }
      );

      const result = await response.json();
      
      if (response.ok && result.success) {
        setExistingSubmission({
          id: result.data.submissionId,
          submission: submission.trim(),
          submissionType,
          fileName: fileName || undefined,
          submittedAt: new Date().toISOString(),
          status: result.data.feedback ? 'graded' : 'submitted',
          feedback: result.data.feedback,
          gradedAt: result.data.feedback ? new Date().toISOString() : undefined,
          gradedBy: result.data.feedback ? 'AI_Professor' : undefined
        });

        if (result.data.feedback) {
          setShowFeedback(true);
          toast.success('Assignment submitted and graded successfully!');
        } else {
          toast.success('Assignment submitted successfully! It will be reviewed shortly.');
        }
        
        if (onComplete) {
          onComplete();
        }
      } else {
        toast.error(result.message || 'Failed to submit assignment');
      }
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // In a real app, you'd upload to cloud storage and get a URL
      // For demo purposes, we'll just read the file content
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setSubmission(content);
        setFileName(file.name);
        setSubmissionType('file');
      };
      reader.readAsText(file);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDueDateStatus = () => {
    if (!assignment.dueDate) return null;
    
    const dueDate = new Date(assignment.dueDate);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'overdue', text: `Overdue by ${Math.abs(diffDays)} day(s)`, color: 'text-red-600' };
    } else if (diffDays === 0) {
      return { status: 'today', text: 'Due today', color: 'text-orange-600' };
    } else if (diffDays <= 3) {
      return { status: 'soon', text: `Due in ${diffDays} day(s)`, color: 'text-yellow-600' };
    } else {
      return { status: 'future', text: `Due in ${diffDays} day(s)`, color: 'text-green-600' };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  const dueDateStatus = getDueDateStatus();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Assignment Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <DocumentTextIcon className="h-6 w-6 text-indigo-600 mt-1" />
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{assignment.title}</h3>
              <p className="text-gray-600 mt-1">{assignment.description}</p>
            </div>
          </div>
          
          {existingSubmission && (
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
              existingSubmission.status === 'graded' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {existingSubmission.status === 'graded' ? (
                <CheckCircleIcon className="h-4 w-4" />
              ) : (
                <ClockIcon className="h-4 w-4" />
              )}
              <span className="capitalize">{existingSubmission.status}</span>
            </div>
          )}
        </div>

        {/* Due Date */}
        {dueDateStatus && (
          <div className={`flex items-center space-x-2 mt-3 ${dueDateStatus.color}`}>
            <ClockIcon className="h-4 w-4" />
            <span className="text-sm font-medium">{dueDateStatus.text}</span>
            {dueDateStatus.status === 'overdue' && (
              <ExclamationTriangleIcon className="h-4 w-4" />
            )}
          </div>
        )}
      </div>

      {/* Assignment Instructions */}
      <div className="p-6 border-b border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Instructions</h4>
        <div className="prose prose-sm max-w-none text-gray-700">
          <p>{assignment.instructions}</p>
        </div>
        
        {assignment.rubric && assignment.rubric.length > 0 && (
          <div className="mt-4">
            <h5 className="font-medium text-gray-900 mb-2">Grading Rubric</h5>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {assignment.rubric.map((criteria: any, index: number) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <span className="font-medium text-sm">{criteria.criteria}</span>
                    <p className="text-xs text-gray-600">{criteria.description}</p>
                  </div>
                  <span className="text-sm text-gray-700">{criteria.maxPoints} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submission Section */}
      <div className="p-6">
        {!existingSubmission ? (
          // New submission form
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Your Submission</h4>
            
            {/* Submission Type Selector */}
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="text"
                  checked={submissionType === 'text'}
                  onChange={(e) => setSubmissionType(e.target.value as 'text')}
                  className="mr-2"
                />
                Text Entry
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="file"
                  checked={submissionType === 'file'}
                  onChange={(e) => setSubmissionType(e.target.value as 'file')}
                  className="mr-2"
                />
                File Upload
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="url"
                  checked={submissionType === 'url'}
                  onChange={(e) => setSubmissionType(e.target.value as 'url')}
                  className="mr-2"
                />
                URL/Link
              </label>
            </div>

            {/* Submission Content */}
            {submissionType === 'text' && (
              <textarea
                value={submission}
                onChange={(e) => setSubmission(e.target.value)}
                placeholder="Enter your assignment submission here..."
                rows={8}
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            )}

            {submissionType === 'file' && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <div className="flex flex-col items-center">
                  <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors">
                    Choose File
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".txt,.doc,.docx,.pdf"
                    />
                  </label>
                  {fileName && (
                    <p className="mt-2 text-sm text-gray-600">Selected: {fileName}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Supported formats: TXT, DOC, DOCX, PDF (max {assignment.maxFileSize || 10}MB)
                  </p>
                </div>
              </div>
            )}

            {submissionType === 'url' && (
              <input
                type="url"
                value={submission}
                onChange={(e) => setSubmission(e.target.value)}
                placeholder="https://..."
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !submission.trim()}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Assignment'}
            </button>
          </div>
        ) : (
          // Existing submission display
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Your Submission</h4>
              <span className="text-sm text-gray-500">
                Submitted {formatDate(existingSubmission.submittedAt)}
              </span>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-700">
                <strong>Type:</strong> {existingSubmission.submissionType}
                {existingSubmission.fileName && (
                  <span className="ml-2">({existingSubmission.fileName})</span>
                )}
              </div>
              <div className="mt-3">
                <div className="max-h-32 overflow-y-auto text-sm text-gray-800">
                  {existingSubmission.submission}
                </div>
              </div>
            </div>

            {/* Feedback Section */}
            {existingSubmission.status === 'graded' && existingSubmission.feedback && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-gray-900">AI Professor Feedback</h5>
                  <button
                    onClick={() => setShowFeedback(!showFeedback)}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    {showFeedback ? 'Hide' : 'Show'} Feedback
                  </button>
                </div>

                {showFeedback && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700">
                        {existingSubmission.feedback}
                      </div>
                    </div>
                    {existingSubmission.gradedAt && (
                      <div className="mt-4 pt-4 border-t border-blue-200 text-xs text-blue-600">
                        Graded by {existingSubmission.gradedBy} on {formatDate(existingSubmission.gradedAt)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {existingSubmission.status === 'submitted' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-yellow-600 mr-2" />
                  <span className="text-yellow-800 text-sm">
                    Your submission is being reviewed. You'll receive feedback shortly.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentComponent;